import { isNative } from "@stepapp/core";
import {
  Divider,
  LanguageDropdown,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  TypographyVariants,
  useBreakpoints,
} from "@stepapp/ui";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View } from "react-native";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";
import { openDrawer } from "../../helpers/drawer";
import { useAppDispatch } from "../../hooks";
import { setAppConfig } from "../../store/appConfig";
import IntercomButton from "./IntercomButton";

type Props = {
  items: { label: string; onPress: () => void; font?: TypographyVariants }[];
  showMobileNavigation: boolean;
};

const HeaderDropdownButton = ({ showMobileNavigation, items }: Props) => {
  const menuDropdownRef = React.useRef<View>(null);
  const { md, lg } = useBreakpoints();
  const appDispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  const openMenuDropdown = useCallback(() => {
    setIsMenuDropdownOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isNative) return;

    document?.addEventListener("click", handleClick);
    return () => document?.removeEventListener("click", handleClick);
    function handleClick(e: ToDo) {
      // @ts-expect-error
      setIsMenuDropdownOpen(menuDropdownRef?.current?.contains?.(e.target));
    }
  }, []);

  const handleLanguageChange = useCallback(
    (language: string) => {
      setIsLangDropdownOpen(false);

      appDispatch(setAppConfig({ pickedLanguage: language }));
      i18n.changeLanguage(language);
    },
    [appDispatch, i18n.changeLanguage],
  );

  const menuDropdownItems = useMemo(
    () =>
      items.map((item, i) => {
        const penultItem = items?.length - 2 === i;
        return (
          <Touchable
            key={item.label}
            onPress={() => {
              item.onPress();
              setIsMenuDropdownOpen(false);
            }}
          >
            <Typography numberOfLines={1} type="primary" variant={item.font || "paragraph"}>
              {item.label}
            </Typography>

            {penultItem && (
              <Divider spaceless color={palette.common.disabled} style={styles.divider} />
            )}
          </Touchable>
        );
      }),
    [items],
  );

  return (
    <>
      <LanguageDropdown
        currentLanguage={i18n.language}
        setIsLangDropdownOpen={setIsLangDropdownOpen}
        isVisible={isLangDropdownOpen}
        onPress={handleLanguageChange}
      />

      {lg && <IntercomButton />}

      <Touchable
        onPress={showMobileNavigation ? openDrawer : openMenuDropdown}
        testID="menuDropdown"
        style={[styles.menuContainer, md && { marginLeft: spacing.unit }]}
      >
        <SvgIcon color={palette.primary.accent} name="menu" size={24} style={styles.menuIcon} />
        <Typography center type="accent" variant="newBodyBold" style={styles.menuText}>
          {t("common.menu")}
        </Typography>
      </Touchable>

      {isMenuDropdownOpen && (
        <Animated.View
          entering={FadeIn.duration(100)}
          exiting={FadeOut.duration(50)}
          ref={menuDropdownRef}
          style={styles.menuDropdown}
        >
          {menuDropdownItems}
        </Animated.View>
      )}
    </>
  );
};

export default React.memo(HeaderDropdownButton);

const styles = StyleSheet.create({
  menuContainer: {
    borderColor: palette.primary.accent,
    borderRadius: spacing.radius * 3,
    borderWidth: spacing.border * 2,
    paddingHorizontal: spacing.unit * 2,
    height: 41,
    alignContent: "center",
    flexDirection: "row",
  },
  menuIcon: {
    marginRight: spacing.unit,
    marginVertical: "auto",
  },
  menuText: {
    marginVertical: "auto",
  },
  menuDropdown: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius,
    borderWidth: spacing.border,
    borderColor: palette.secondary.grey,
    elevation: 2,
    gap: spacing.unit * 2,
    position: "absolute",
    padding: spacing.unit * 3,
    right: spacing.unit * 1.5,
    top: spacing.unit * 9,
    ...Platform.select({
      default: {
        shadowColor: palette.common.overlay,
        shadowOffset: { height: 2, width: 2 },
      },
      web: {
        boxShadow:
          "0px 41px 80px 0px rgba(0, 0, 0, 0.09), 0px 15.793px 25.481px 0px rgba(0, 0, 0, 0.05), 0px 3.341px 6.519px 0px rgba(0, 0, 0, 0.04)",
      },
    }),
  },
  divider: {
    marginTop: spacing.unit * 2,
  },
});
